import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  List,
  Result,
  Row,
  Space,
  Spin,
  Tabs,
  Typography,
  notification,
} from 'antd';
import React, { useEffect, useState } from 'react';
import ImageGallery from 'react-image-gallery';

import {
  useGetActiveAuctionQuery,
  useGetLiveAuctionStatusQuery,
  useGoToNextAuctionItemMutation,
  usePauseAuctionMutation,
  useResumeAuctionMutation,
  useStartAuctionMutation,
  useStopAuctionMutation,
} from '../../../app/api/auctionApiSlice';
import StartAuction from '../components/StartAuction';
import LiveAuctionStatus from '../../../core/models/LiveAuctionStatus';
import AuctionItem from '../../../core/models/AuctionItem';
import { useNavigate } from 'react-router-dom';
import ViewVehicleDetails from '../../vehicles/containers/ViewVehicleDetails';
import { convertToCurrencyString } from '../../../utils/currencyUtil';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { isJson } from '../../../utils/jsonUtil';
import { stat } from 'fs';

const { Title, Text } = Typography;

const ws = new WebSocket(`${process.env.REACT_APP_AUTOSTREAM_WS!}/auction/id`);

const LiveAuction: React.FC = () => {
  const navigate = useNavigate();

  const { data: auction, isLoading: isAuctionLoading } = useGetActiveAuctionQuery();
  const { data: initialAuctionStatus, isLoading: isAuctionStatusLoading } =
    useGetLiveAuctionStatusQuery(auction ? auction.id : skipToken);

  const [startAuction, { isLoading: isStarting }] = useStartAuctionMutation();
  const [goToNextAuction, { isLoading: isNextItemLoading }] = useGoToNextAuctionItemMutation();
  const [pauseAuction, { isLoading: isPausing }] = usePauseAuctionMutation();
  const [resumeAuction, { isLoading: isResuming }] = useResumeAuctionMutation();
  const [stopAuction, { isLoading: isStopping }] = useStopAuctionMutation();

  const [liveAuctionStatus, setLiveAuctionStatus] = useState<LiveAuctionStatus>();
  const [activeAuctionItem, setActiveAuctionItem] = useState<AuctionItem>();

  useEffect(() => {
    ws.onopen = (event) => {
      console.log('Connected to WebSocket');
    };

    ws.onmessage = (event) => {
      if (!isJson(event.data)) return;

      try {
        const status: LiveAuctionStatus = JSON.parse(event.data);

        if (status.event === 'MoveToNext' || status.event === 'Stop') {
          if (status.auctionStatus?.previousAuctionItemStatus?.status === 'Sold') {
            notification.success({
              message: 'Previous Vehicle Auction Status',
              description: (
                <>
                  Previous vehicle was{' '}
                  <Text type='success'>
                    {status.auctionStatus?.previousAuctionItemStatus?.status.toUpperCase()}
                  </Text>
                </>
              ),
            });
          } else if (status.auctionStatus?.previousAuctionItemStatus?.status === 'Unsold') {
            notification.warning({
              message: 'Previous Vehicle Auction Status',
              description: (
                <>
                  Previous vehicle was{' '}
                  <Text type='warning'>
                    {status.auctionStatus?.previousAuctionItemStatus?.status.toUpperCase()}
                  </Text>
                </>
              ),
            });
          }
        }

        setAuctionStatus(status);
      } catch (error: any) {
        console.log(error);
      }
    };
  });

  useEffect(() => {
    if (initialAuctionStatus) {
      if (initialAuctionStatus)
        setAuctionStatus({ auctionStatus: initialAuctionStatus, event: 'Start' });
    }
  }, [initialAuctionStatus]);

  const setAuctionStatus = (status: LiveAuctionStatus): void => {
    setLiveAuctionStatus(status);

    const activeItem = getActiveAuctionItem(status);

    if (activeItem) setActiveAuctionItem(activeItem);

    if (liveAuctionStatus?.auctionStatus?.status.id === 4) {
      navigate(`/auctions/${auction?.id ?? ''}`);
    }
  };

  const getActiveAuctionItem = (auctionStatus: LiveAuctionStatus): AuctionItem | undefined => {
    if (auction && auctionStatus)
      return auction.auctionItems.find(
        (item: AuctionItem) => item.id === auctionStatus?.auctionStatus?.activeAuctionItemId,
      );
  };

  const handleStartAuction = (): void => {
    if (auction)
      startAuction(auction.id)
        .unwrap()
        .then((result: any) => {
          setLiveAuctionStatus({ auctionStatus: result, event: 'Start' });

          const activeItem = getActiveAuctionItem({
            auctionStatus: result,
            event: 'Start',
          });
          setActiveAuctionItem(activeItem);
        })
        .catch((error) => {
          console.log(error);
        });
  };

  const handleNextAuctionItem = (): void => {
    if (auction)
      goToNextAuction(auction.id)
        .unwrap()
        .then((result: any) => {
          setLiveAuctionStatus({ auctionStatus: result, event: 'MoveToNext' });

          const activeItem = getActiveAuctionItem({ auctionStatus: result, event: 'MoveToNext' });
          setActiveAuctionItem(activeItem);
        })
        .catch((error) => {
          console.log(error);
        });
  };

  const handlePauseAuction = (): void => {
    if (auction)
      pauseAuction(auction.id)
        .unwrap()
        .then((result: any) => {
          setLiveAuctionStatus({ auctionStatus: result, event: 'Pause' });
        })
        .catch((error) => {
          console.log(error);
        });
  };

  const handleResumeAuction = (): void => {
    if (auction)
      resumeAuction(auction.id)
        .unwrap()
        .then((result: any) => {
          setLiveAuctionStatus({ auctionStatus: result, event: 'Resume' });
        })
        .catch((error) => {
          console.log(error);
        });
  };

  const handleStopAuction = (): void => {
    if (auction)
      stopAuction(auction.id)
        .unwrap()
        .then(() => {
          navigate(`/auctions/${auction?.id ?? ''}`);
        })
        .catch((error) => {
          console.log(error);
        });
  };

  if (isAuctionLoading || isAuctionStatusLoading)
    return (
      <Row justify={'center'}>
        <Space direction='vertical' align='center'>
          <Spin />
          <span>Loading...</span>
        </Space>
      </Row>
    );

  if (!auction) return <Result status='info' title='No Active Auction'></Result>;

  if (auction?.status.id === 1) {
    return <StartAuction auction={auction} onStart={handleStartAuction} loading={isStarting} />;
  }

  return (
    <div>
      {(auction?.status.id === 2 || auction?.status.id === 3) && (
        <Row>
          <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              {liveAuctionStatus?.auctionStatus?.status.id !== 3 && (
                <Button type='ghost' danger onClick={handlePauseAuction} loading={isPausing}>
                  Pause Auction
                </Button>
              )}
              {liveAuctionStatus?.auctionStatus?.status.id === 3 && (
                <Button type='ghost' danger onClick={handleResumeAuction} loading={isResuming}>
                  Resume Auction
                </Button>
              )}
              {liveAuctionStatus?.auctionStatus?.nextAuctionItemId &&
                liveAuctionStatus?.auctionStatus?.status.id !== 3 && (
                  <Button
                    type='primary'
                    onClick={handleNextAuctionItem}
                    loading={isNextItemLoading}
                  >
                    Next Vehicle
                  </Button>
                )}
              <Button type='primary' danger onClick={handleStopAuction} loading={isStopping}>
                End Auction
              </Button>
            </Space>
          </Col>
        </Row>
      )}
      {(auction?.status.id === 2 || auction?.status.id === 3) &&
        activeAuctionItem !== undefined && (
          <>
            <Row>
              <Col span={12}>
                <Text style={{ fontWeight: '600', fontSize: 18 }}>{`${
                  activeAuctionItem?.vehicle.name ?? ''
                }`}</Text>
              </Col>
            </Row>
            <br />
            <Row gutter={12}>
              <Col span={14} offset={2}>
                <Tabs>
                  <Tabs.TabPane tab='Vehicle Details' key='vehicle'>
                    <ImageGallery
                      items={activeAuctionItem.vehicle.images?.map((image) => ({
                        original: image.filePath,
                        thumbnail: image.filePath,
                      }))}
                      showFullscreenButton={false}
                      showPlayButton={false}
                      lazyLoad={true}
                    />
                    <ViewVehicleDetails vehicle={activeAuctionItem.vehicle} layout='vertical' />
                  </Tabs.TabPane>
                  {activeAuctionItem?.vehicle?.inspectionReportUrl && (
                    <Tabs.TabPane tab='Grading Report' key='inspection'>
                      <iframe
                        width={600}
                        height={900}
                        src={`${activeAuctionItem?.vehicle?.inspectionReportUrl}?view=fitH`}
                      ></iframe>
                    </Tabs.TabPane>
                  )}
                </Tabs>
              </Col>
              <Col span={6}>
                <Card title={<Title level={4}>Live Auction</Title>}>
                  <Descriptions
                    bordered={false}
                    layout='vertical'
                    labelStyle={{ fontWeight: 'bold' }}
                    column={2}
                    size='small'
                    colon={false}
                  >
                    <Descriptions.Item label='Starting Price'>
                      {convertToCurrencyString(activeAuctionItem.startingPrice)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Reserve Price'>
                      {convertToCurrencyString(activeAuctionItem.reservePrice)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Seller'>
                      {activeAuctionItem?.vehicle?.seller?.companyName ||
                        activeAuctionItem?.vehicle?.seller?.personInCharge}
                    </Descriptions.Item>
                  </Descriptions>
                  <br />
                  <List
                    itemLayout='horizontal'
                    size='small'
                    style={{ overflow: 'auto', maxHeight: '100vh' }}
                  >
                    {(liveAuctionStatus?.auctionStatus?.activeAuctionItemBids === null ||
                      (liveAuctionStatus?.auctionStatus?.activeAuctionItemBids &&
                        liveAuctionStatus?.auctionStatus?.activeAuctionItemBids?.length <= 0)) && (
                      <Alert message='No bids' type='warning' />
                    )}
                    {liveAuctionStatus?.auctionStatus?.activeAuctionItemBids?.map((bid) => (
                      <List.Item key={bid.bidPrice}>
                        <List.Item.Meta
                          title={convertToCurrencyString(bid.bidPrice)}
                          description={bid.buyer.name}
                        />
                      </List.Item>
                    ))}
                  </List>
                </Card>
              </Col>
            </Row>
          </>
        )}
    </div>
  );
};

export default LiveAuction;
